import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ChartRenderer from '../components/ChartRenderer';
import {
    Typography,
    Box,
    CircularProgress,
    Alert,
    Divider,
    Card,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemText,
    Container,
    AppBar,
    Toolbar,
    Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import apiService from '../services/api';
import MethodologyAppendix from '../components/MethodologyAppendix';
import LintWarning from '../components/LintWarning';
import logo from '../assets/logo.png';

const SharedReport = () => {
    const { reportId } = useParams();
    const [report, setReport] = useState(null);
    const [reportMeta, setReportMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [expandedSections, setExpandedSections] = useState({});

    useEffect(() => {
        const fetchSharedReport = async () => {
            try {
                setLoading(true);
                setError('');
                
                const response = await apiService.getSharedReport(reportId);
                console.log('Shared report response:', response.data);
                
                if (response.data.report_data) {
                    setReport(response.data.report_data);
                    setReportMeta({
                        brand: response.data.brand,
                        category: response.data.category,
                        country: response.data.country,
                        year: response.data.year,
                        created_at: response.data.created_at,
                        title: response.data.title
                    });
                    
                    // Initialize first section as expanded
                    if (response.data.report_data.sections) {
                        const initialExpanded = {};
                        initialExpanded[0] = true;
                        setExpandedSections(initialExpanded);
                    }
                } else {
                    setError('Report data not available');
                }
            } catch (err) {
                console.error('Error loading shared report:', err);
                if (err.response?.status === 404) {
                    setError('Report not found or not available for sharing');
                } else {
                    setError('Failed to load report. Please check the link and try again.');
                }
            } finally {
                setLoading(false);
            }
        };

        if (reportId) {
            fetchSharedReport();
        }
    }, [reportId]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                <AppBar position="static" sx={{ bgcolor: '#5b1256' }}>
                    <Toolbar>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <img src={logo} alt="BrandSpark" style={{ height: '32px', marginRight: '16px' }} />
                            <Typography variant="h6" component="div">
                                BrandSpark ® Trust Survey - Shared Report
                            </Typography>
                        </Box>
                    </Toolbar>
                </AppBar>
                <Container maxWidth="lg" sx={{ py: 4, flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <CircularProgress size={60} />
                        <Typography variant="h6" sx={{ mt: 2 }}>
                            Loading report...
                        </Typography>
                    </Box>
                </Container>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                <AppBar position="static" sx={{ bgcolor: '#5b1256' }}>
                    <Toolbar>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <img src={logo} alt="BrandSpark" style={{ height: '32px', marginRight: '16px' }} />
                            <Typography variant="h6" component="div">
                                BrandSpark ® Trust Survey - Shared Report
                            </Typography>
                        </Box>
                    </Toolbar>
                </AppBar>
                <Container maxWidth="lg" sx={{ py: 4, flexGrow: 1 }}>
                    <Alert severity="error" sx={{ mt: 4 }}>
                        {error}
                    </Alert>
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            {/* Header */}
            <AppBar position="static" sx={{ bgcolor: '#5b1256' }}>
                <Toolbar>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <img src={logo} alt="BrandSpark" style={{ height: '32px', marginRight: '16px' }} />
                        <Typography variant="h6" component="div">
                            BrandSpark ® Trust Survey - Shared Report
                        </Typography>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Main Content */}
            <Container maxWidth="lg" sx={{ py: 4, flexGrow: 1 }}>
                {/* AI Disclaimer */}
                <Alert severity="warning" sx={{ mb: 3 }}>
                    <strong>Disclaimer:</strong> TrustSpark.ai uses an AI model and can hallucinate or make mistakes. Please double-check all outputs. By using TrustSpark.ai, you acknowledge the possibility of error and agree to assume all liability for decisions made on the basis of AI outputs.
                </Alert>

                {report && reportMeta && (
                    <>
                        {/* Data-vs-claims linter results (view-only on shared reports —
                            no re-generation available). */}
                        <LintWarning lint={report.lint} />

                        {/* Report Header */}
                        <Card sx={{ mb: 4 }}>
                            <CardContent>
                                <Typography variant="h4" component="h1" gutterBottom align="center">
                                    {report.title}
                                </Typography>
                                
                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                                    <Chip label={`Brand: ${reportMeta.brand}`} variant="outlined" />
                                    {reportMeta.category && <Chip label={`Category: ${reportMeta.category}`} variant="outlined" />}
                                    {reportMeta.country && <Chip label={`Country: ${reportMeta.country}`} variant="outlined" />}
                                    <Chip 
                                        label={`Generated: ${new Date(reportMeta.created_at).toLocaleDateString()}`} 
                                        variant="outlined" 
                                    />
                                </Box>
                                
                                <Divider sx={{ my: 2 }} />
                                
                                <Typography variant="body1" paragraph>
                                    <strong>Executive Summary:</strong> {report.executive_summary}
                                </Typography>

                                {report.data_highlights && report.data_highlights.length > 0 && (
                                    <Box sx={{ mt: 3, mb: 2 }}>
                                        <Typography variant="h6" gutterBottom>
                                            Key Data Highlights
                                        </Typography>
                                        <List>
                                            {report.data_highlights.map((highlight, index) => (
                                                <ListItem key={index} sx={{ py: 0.5 }}>
                                                    <ListItemText primary={`• ${highlight}`} />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>

                        {/* Expand/Collapse Controls */}
                        {report.sections && report.sections.length > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{ 
                                            cursor: 'pointer', 
                                            color: 'primary.main',
                                            textDecoration: 'underline',
                                            '&:hover': { color: 'primary.dark' }
                                        }}
                                        onClick={() => {
                                            const newExpandedSections = {};
                                            report.sections.forEach((_, index) => {
                                                newExpandedSections[index] = true;
                                            });
                                            setExpandedSections(newExpandedSections);
                                        }}
                                    >
                                        Expand All
                                    </Typography>
                                    <Typography variant="body2" sx={{ mx: 1 }}>|</Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ 
                                            cursor: 'pointer', 
                                            color: 'primary.main',
                                            textDecoration: 'underline',
                                            '&:hover': { color: 'primary.dark' }
                                        }}
                                        onClick={() => {
                                            const newExpandedSections = {};
                                            report.sections.forEach((_, index) => {
                                                newExpandedSections[index] = false;
                                            });
                                            setExpandedSections(newExpandedSections);
                                        }}
                                    >
                                        Collapse All
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {/* Report Sections */}
                        {report.sections && report.sections.map((section, index) => (
                            <Accordion
                                key={index}
                                expanded={expandedSections[index] ?? (index === 0)}
                                onChange={(_, isExpanded) => {
                                    setExpandedSections(prev => ({
                                        ...prev,
                                        [index]: isExpanded
                                    }));
                                }}
                                sx={{ mb: 1 }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls={`panel${index}-content`}
                                    id={`panel${index}-header`}
                                >
                                    <Typography variant="h6">{section.title}</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {section.intro && (
                                        <Typography variant="body1" paragraph sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                            {section.intro}
                                        </Typography>
                                    )}

                                    <Typography variant="body1" paragraph>
                                        {section.content}
                                    </Typography>

                                    {section.subsections && section.subsections.length > 0 && (
                                        <Box sx={{ mt: 2 }}>
                                            {section.subsections.map((subsection, subIndex) => (
                                                <Box key={subIndex} sx={{ mb: 3, ml: 2 }}>
                                                    <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                                                        {subsection.title}
                                                    </Typography>
                                                    <Typography variant="body2" paragraph>
                                                        {subsection.content}
                                                    </Typography>

                                                    {subsection.key_findings && subsection.key_findings.length > 0 && (
                                                        <Box sx={{ mt: 2, bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                                                            <Typography variant="subtitle2" gutterBottom>
                                                                Key Findings:
                                                            </Typography>
                                                            <List dense>
                                                                {subsection.key_findings.map((finding, findingIdx) => (
                                                                    <ListItem key={findingIdx} sx={{ py: 0.5 }}>
                                                                        <ListItemText primary={`• ${finding}`} />
                                                                    </ListItem>
                                                                ))}
                                                            </List>
                                                        </Box>
                                                    )}

                                                    {subsection.visualizations && subsection.visualizations.length > 0 && (
                                                        <Box sx={{ mt: 2 }}>
                                                            {subsection.visualizations.map((visualization, vizIndex) => (
                                                                <Box key={vizIndex} sx={{ mb: vizIndex < subsection.visualizations.length - 1 ? 3 : 0 }}>
                                                                    <Typography variant="subtitle2" gutterBottom>
                                                                        {visualization.title}
                                                                    </Typography>
                                                                    <ChartRenderer visualization={visualization} />
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    )}
                                                </Box>
                                            ))}
                                        </Box>
                                    )}

                                    {section.key_findings && section.key_findings.length > 0 && (
                                        <Box sx={{ mt: 2, bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                                            <Typography variant="subtitle1" gutterBottom>
                                                Key Findings:
                                            </Typography>
                                            <List dense>
                                                {section.key_findings.map((finding, idx) => (
                                                    <ListItem key={idx} sx={{ py: 0.5 }}>
                                                        <ListItemText primary={`• ${finding}`} />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                    )}

                                    {section.plan_sections && section.plan_sections.length > 0 && (
                                        <Box sx={{ mt: 2, bgcolor: '#f0f8ff', p: 2, borderRadius: 1 }}>
                                            <List dense>
                                                {section.plan_sections.map((planSection, idx) => {
                                                    // Handle both string and object formats
                                                    if (typeof planSection === 'string') {
                                                        return (
                                                            <ListItem key={idx} sx={{ py: 0.5 }}>
                                                                <ListItemText primary={`• ${planSection}`} />
                                                            </ListItem>
                                                        );
                                                    } else if (typeof planSection === 'object' && planSection !== null) {
                                                        // Handle object format with title and content
                                                        const title = planSection.title || planSection.name || `Section ${idx + 1}`;
                                                        const content = planSection.content || planSection.description || JSON.stringify(planSection);
                                                        return (
                                                            <ListItem key={idx} sx={{ py: 1, alignItems: 'flex-start' }}>
                                                                <ListItemText 
                                                                    primary={
                                                                        <Box>
                                                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                                                                {title}
                                                                            </Typography>
                                                                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                                                {content}
                                                                            </Typography>
                                                                        </Box>
                                                                    }
                                                                />
                                                            </ListItem>
                                                        );
                                                    }
                                                    return null;
                                                })}
                                            </List>
                                        </Box>
                                    )}

                                    {section.claims && section.claims.length > 0 && (
                                        <Box sx={{ mt: 2, bgcolor: '#fff8e1', p: 2, borderRadius: 1 }}>
                                            <Typography variant="subtitle1" gutterBottom>
                                                Marketing Claims:
                                            </Typography>
                                            <List dense>
                                                {section.claims.map((claim, idx) => (
                                                    <ListItem key={idx} sx={{ py: 0.5 }}>
                                                        <ListItemText primary={`${idx + 1}. ${claim}`} />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                    )}

                                    {section.visualizations && section.visualizations.length > 0 && (
                                        <Box sx={{ mt: 3 }}>
                                            {section.visualizations.map((visualization, vizIndex) => (
                                                <Box key={vizIndex} sx={{ mb: vizIndex < section.visualizations.length - 1 ? 4 : 0 }}>
                                                    <Typography variant="subtitle1" gutterBottom>
                                                        {visualization.title}
                                                    </Typography>
                                                    <ChartRenderer visualization={visualization} />
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        ))}

                        {/* Recommendations */}
                        {report.recommendations && report.recommendations.length > 0 && (
                            <Card sx={{ mt: 4 }}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Recommendations
                                    </Typography>
                                    <List>
                                        {report.recommendations.map((recommendation, index) => (
                                            <ListItem key={index} sx={{ py: 0.5 }}>
                                                <ListItemText primary={`${index + 1}. ${recommendation}`} />
                                            </ListItem>
                                        ))}
                                    </List>
                                </CardContent>
                            </Card>
                        )}

                        {/* Methodology Appendix */}
                        <MethodologyAppendix country={reportMeta?.country} year={reportMeta?.year} />

                        {/* Contact Info */}
                        <Box sx={{ mt: 4, py: 3, px: 2, borderTop: '1px solid #eee', textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                To learn more about the BrandSpark Most Trusted Awards program, methodology, or for a full list of winning brands, visit{' '}
                                <a href="https://www.brandsparkmosttrusted.com" target="_blank" rel="noopener noreferrer">
                                    www.brandsparkmosttrusted.com
                                </a>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Contact: Robert Levy, BrandSpark{' '}
                                <a href="mailto:RLevy@BrandSpark.com">RLevy@BrandSpark.com</a>
                            </Typography>
                        </Box>

                        {/* Footer */}
                        <Box sx={{ mt: 2, py: 3, textAlign: 'center', borderTop: '1px solid #eee' }}>
                            <Typography variant="body2" color="text.secondary">
                                © 2025 BrandSpark International Inc. All Rights Reserved
                            </Typography>
                        </Box>
                    </>
                )}
            </Container>
        </Box>
    );
};

export default SharedReport; 